<template>
    <section class="frontline-manager-dashboard-card" :aria-label="title">
        <div class="card-body">
            <header class="card-header">
                <FontAwesomeIcon :icon="icon" class="card-header--icon" />
                <h2 class="card-header--title">{{ title }}</h2>
                <slot name="title-suffix"></slot>
                <slot name="header" />
                <span
                    v-if="tooltip"
                    v-tooltip="{
                        value: tooltip,
                        pt: {
                            root: {
                                style: {
                                    'max-width': '300px',
                                },
                            },
                            text: {
                                style: {
                                    background: 'white',
                                    color: '#334054',
                                    boxShadow:
                                        '0px 0px 10px 3px rgba(0, 0, 0, 0.2)',
                                    'text-align': 'center',
                                },
                            },
                            arrow: {
                                style: {
                                    'border-top-color': 'white',
                                    'border-left-color': 'white',
                                    'border-right-color': 'white',
                                },
                            },
                        },
                    }"
                    class="card-header--tooltip"
                >
                    <i class="pi pi-question-circle" />
                </span>
            </header>
            <slot v-if="!showError" name="content" />
            <!-- Check if there is custom error state -->
            <slot v-else-if="$slots.errorContent" name="errorContent" />
            <div v-else class="error-container">
                <p class="error-container--error-message">
                    Whoops, there’s an error... 😅
                </p>
                <Button
                    label="Reload"
                    class="error-container--reload-button"
                    outlined
                    severity="secondary"
                    @click="onReloadClick"
                />
            </div>
        </div>
        <slot v-if="!showError" name="contentNoPadding" />
        <footer v-if="showFooter" class="footer">
            <div class="response-count">
                {{ responseCountLabel }}
            </div>
            <Button
                v-if="actionLabel"
                link
                class="card-action-button"
                data-testid="fm-dash-card-action-button"
                :label="actionLabel"
                @click="onActionClick"
            />
        </footer>
    </section>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { IconDefinition } from 'fontawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from 'fontawesome/vue-fontawesome'
import Button from 'primevue/button'

interface Props {
    icon: IconDefinition
    title: string
    showFooter?: boolean
    responseCount?: number | undefined
    actionLabel?: string
    onActionClick?: Function
    showError?: boolean
    onReloadClick?: Function
    tooltip?: string
}
const props = withDefaults(defineProps<Props>(), {
    showFooter: true,
    showError: false,
    tooltip: '',
    responseCount: undefined,
    actionLabel: '',
    onActionClick: () => ({}),
    onReloadClick: () => ({}),
})

const responseCountLabel = computed(() => {
    if (props.responseCount === undefined) {
        return ''
    }
    const formattedCount = props.responseCount.toLocaleString()
    return `${formattedCount} ${props.responseCount === 1 ? 'response' : 'responses'}`
})
</script>

<style lang="less" scoped>
@import '~@/styles/prime-vue/variables.less';
@import '~@/styles/rain/variables.less';

.frontline-manager-dashboard-card {
    background: white;
    border: 1px solid var(--p-gray-200);
    box-shadow: @shadow-xs;
    border-radius: @radius-xl;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.card-body {
    padding: @spacing-xl;
    display: flex;
    flex-direction: column;
    gap: @spacing-md;
    flex: 1;
    container-name: dashboardCard;
    container-type: inline-size;
}

.card-header {
    display: flex;
    gap: @spacing-lg;
    align-items: center;

    &--title {
        margin: 0;
        color: var(--p-gray-900);
        font-size: @fontSize-lg;
        font-weight: @fontWeight-bold;
        line-height: @lineHeight-lg;
        overflow: hidden;
        text-wrap: nowrap;
        text-overflow: ellipsis;
    }
    &--icon {
        color: var(--p-primary-500);
        height: @display-xs;
    }
    &--tooltip {
        color: var(--p-gray-400);
    }
}

.error-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: @spacing-3xl;
    gap: @spacing-xl;
    flex-wrap: wrap;
    align-content: center;

    &--error-message {
        font-size: @fontSize-lg;
        line-height: @lineHeight-lg;
        font-weight: @fontWeight-bold;
        color: var(--p-gray-900);
        margin: 0;
        text-align: center;
    }

    &--reload-button {
        height: fit-content;
        padding: 10px 14px;
        font-size: @fontSize-sm;
        color: var(--p-gray-700);
        box-shadow: @shadow-xs;
        text-transform: capitalize;
    }
}

.footer {
    box-sizing: border-box;
    display: flex;
    min-height: 50px;
    flex-grow: 0;
    justify-content: space-between;
    align-items: center;
    gap: @spacing-xl;
    padding: @spacing-xl @spacing-3xl @spacing-xl @spacing-xl;
    border-top: 1px solid var(--p-gray-200);

    .response-count {
        font-size: @fontSize-base;
        line-height: @lineHeight-base;
        font-weight: @fontWeight-medium;
        color: var(--p-gray-600);
    }

    .card-action-button {
        padding: 0;
        color: var(--p-primary-500);
        font-weight: @fontWeight-bold;
        font-size: @fontSize-base;
        line-height: @lineHeight-base;
    }
}

@media (min-width: @desktopBreakpoint) {
    .card-body {
        padding: @spacing-3xl;
        gap: @spacing-xl;
    }

    .card-header {
        &--icon {
            height: @display-sm;
        }
    }
}
</style>
